import { create } from 'zustand';
import { detachPushFromAccount, disablePush, enablePush, getPushState, registerServiceWorker } from '../utils/push';
import { SITE_MODE } from '../config';

// Куда вести после входа/регистрации: в новой витрине — личный кабинет,
// в старых витринах — страница заявки на турнир
const AFTER_AUTH_ROUTE = SITE_MODE === 'fed' ? 'profile' : 'register';

// Куда вести сразу после входа в зависимости от типа аккаунта:
// админ → админка, SMM-менеджер → его контент-панель, остальные → fallback
const roleRoute = (userType, fallback) =>
  userType === 'admin' ? 'admin' : userType === 'smm' ? 'smm' : fallback;

export const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5252/api');

// Вход через Google: бэкенд возвращает нас на витрину с токенами во ФРАГМЕНТЕ
// адреса (#oauth=1&access_token=…). Забираем его на этапе импорта модуля, до
// первого рендера: FedApp на монтировании переписывает location.hash под свой
// роутинг, и до эффектов приложения payload бы не дожил.
const OAUTH_REDIRECT = (() => {
  try {
    const hash = window.location.hash || '';
    if (!/(^|[#&])oauth(_error)?=/.test(hash)) return null;
    const p = new URLSearchParams(hash.slice(1));
    // Сразу чистим адресную строку — токены не должны остаться в истории
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return {
      accessToken: p.get('access_token'),
      refreshToken: p.get('refresh_token'),
      isNew: p.get('is_new_user') === '1',
      next: p.get('next') || null,
      error: p.get('oauth_error'),
    };
  } catch (e) {
    return null;
  }
})();

let oauthConsumed = false;

const OAUTH_ERRORS = {
  cancelled: null,                                   // просто закрыл окно — молчим
  banned: 'Аккаунт заблокирован',
  bad_state: 'Ссылка входа устарела, попробуйте ещё раз',
  provider_unavailable: 'Вход через Google временно недоступен',
};

// Хелпер авторизованного запроса
export const authHeaders = (json = true) => {
  const token = localStorage.getItem('access_token');
  const lang = localStorage.getItem('sq_lang') || 'ru';
  const h = { 'Accept-Language': lang };
  if (json) h['Content-Type'] = 'application/json';
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
};

// ===== Обновление сессии =====
// Access-токен живёт 24 часа. Без обновления админка через сутки начинала
// молча терять правки: PUT отвечал 401, контент-стор ловил ошибку и оставлял
// изменение только в localStorage. Теперь любой 401 на авторизованном запросе
// один раз меняется на refresh + повтор, а если и это не вышло — сессия
// чистится с явным сообщением, а не тихо.

let refreshPromise = null;

/** Обновить access-токен по refresh-токену. Параллельные 401 ждут один запрос. */
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${refreshToken}` },
        });
        if (!res.ok) return null;
        const { access_token } = await res.json();
        if (access_token) localStorage.setItem('access_token', access_token);
        return access_token || null;
      } catch (e) {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
};

let expiredNotified = false;

/** Refresh не помог — сессии больше нет. Сообщаем один раз и уводим на вход. */
const onSessionExpired = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  try {
    const s = useAppStore.getState();
    // поллинг уведомлений сам замолкает: его tick проверяет наличие токена
    useAppStore.setState({ user: null, notifications: [], myApplications: [] });
    if (!expiredNotified) {
      expiredNotified = true;
      setTimeout(() => { expiredNotified = false; }, 5000);
      s.fire('Сессия истекла — войдите заново', 'error');
      s.go('auth');
    }
  } catch (e) { /* стор ещё не готов — достаточно того, что токены стёрты */ }
};

/** fetch к API: на 401 авторизованного запроса — refresh и один повтор.
    Неавторизованные вызовы (вход, регистрация) проходят как обычный fetch. */
export const apiFetch = async (url, opts = {}) => {
  const full = String(url).startsWith('http') ? url : `${API_URL}${url}`;
  const headers = opts.headers || {};
  const hadAuth = !!(headers.Authorization || headers.authorization);

  const res = await fetch(full, opts);
  if (res.status !== 401 || !hadAuth) return res;

  const token = await refreshAccessToken();
  if (!token) {
    onSessionExpired();
    return res;
  }
  return fetch(full, { ...opts, headers: { ...headers, 'Authorization': `Bearer ${token}` } });
};

/** Привязать анонимную турнирную заявку к только что вошедшему аккаунту */
const claimPendingApplication = async (token) => {
  const pendingAppId = localStorage.getItem('pending_app_id');
  if (!pendingAppId) return;
  try {
    const body = { app_id: parseInt(pendingAppId) };
    const pendingToken = localStorage.getItem('pending_claim_token');
    if (pendingToken) body.claim_token = pendingToken;
    const res = await apiFetch(`${API_URL}/tournament/apply/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      localStorage.removeItem('pending_app_id');
      localStorage.removeItem('pending_claim_token');
    }
  } catch (_) {}
};

export const useAppStore = create((set, get) => ({
  route: 'home',
  user: null,
  toast: null,
  openTeamModal: false,
  notifications: [],
  _notifPollTimer: null,
  _pushMessageHandler: null,
  myApplications: [],     // заявки текущего пользователя на витринные турниры
  _postAuthRoute: null,   // куда вернуть пользователя после входа/регистрации (ставит витрина)

  setPostAuthRoute: (r) => set({ _postAuthRoute: r }),

  // Загрузка уведомлений текущего пользователя с сервера
  fetchNotifications: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { set({ notifications: [] }); return; }
    try {
      const res = await apiFetch(`${API_URL}/notifications`, { headers: authHeaders(false) });
      if (!res.ok) return;
      const { notifications } = await res.json();
      set({ notifications: notifications || [] });
    } catch (e) {
      console.error('Ошибка загрузки уведомлений:', e);
    }
  },

  // Админ: создать уведомление и разослать Web Push (всем или одному)
  fetchSentNotifications: async () => {
    try {
      const res = await apiFetch(`${API_URL}/admin/notifications?per_page=100`, {
        headers: authHeaders(false),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка загрузки истории');
      const result = await res.json();
      return result.notifications || [];
    } catch (e) {
      get().fire(`Ошибка истории уведомлений: ${e.message}`, 'error');
      return [];
    }
  },

  sendNotification: async ({ to, title, message, type = 'info', link }) => {
    try {
      const res = await apiFetch(`${API_URL}/admin/notifications`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ to, title, message, type, link }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка отправки');
      const result = await res.json();
      get().fire('Уведомление отправлено', 'ok');
      get().fetchNotifications();  // обновляем список (админ тоже получатель рассылок «всем»)
      return {
        ...result.notification,
        delivery: { webPush: result.push_targets || 0, devices: result.device_targets || 0 },
      };
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  markNotificationRead: async (id) => {
    set(s => ({ notifications: s.notifications.map(n => n.id === id ? { ...n, read: true } : n) }));
    try {
      await apiFetch(`${API_URL}/notifications/${id}/read`, { method: 'PUT', headers: authHeaders(false) });
    } catch (e) { /* оптимистичное обновление остаётся */ }
  },

  markAllNotificationsRead: async () => {
    set(s => ({ notifications: s.notifications.map(n => ({ ...n, read: true })) }));
    try {
      await apiFetch(`${API_URL}/notifications/read-all`, { method: 'PUT', headers: authHeaders(false) });
    } catch (e) { /* no-op */ }
  },

  deleteNotification: async (id) => {
    set(s => ({ notifications: s.notifications.filter(n => n.id !== id) }));
    try {
      await apiFetch(`${API_URL}/notifications/${id}`, { method: 'DELETE', headers: authHeaders(false) });
    } catch (e) { /* no-op */ }
  },

  // Включить системные Web Push на этом устройстве (по кнопке)
  enablePushNotifications: async () => {
    const result = await enablePush();
    if (result === 'granted') get().fire('Пуш-уведомления включены', 'ok');
    else if (result === 'denied') get().fire('Уведомления запрещены в настройках браузера', 'error');
    else if (result === 'unsupported') get().fire('Браузер не поддерживает пуш-уведомления', 'error');
    else if (result !== 'default') get().fire('Не удалось включить пуш-уведомления', 'error');
    return result;
  },

  disablePushNotifications: async () => {
    const result = await disablePush();
    if (result === 'disabled') get().fire('Пуш-уведомления отключены на этом устройстве', 'ok');
    else get().fire('Не удалось отключить пуш-уведомления', 'error');
    return result;
  },

  getPushNotificationState: getPushState,

  // Поллинг in-app уведомлений (нет вебсокетов): интервал + при фокусе окна
  startNotificationPolling: () => {
    if (get()._notifPollTimer) return;
    registerServiceWorker();
    const tick = () => { if (localStorage.getItem('access_token')) get().fetchNotifications(); };
    const timer = setInterval(tick, 45000);
    window.addEventListener('focus', tick);
    const pushMessageHandler = (event) => {
      if (event.data?.type === 'PUSH_RECEIVED') tick();
    };
    navigator.serviceWorker?.addEventListener('message', pushMessageHandler);
    set({ _notifPollTimer: timer, _pushMessageHandler: pushMessageHandler });
  },

  go: (r) => {
    set({ route: r });
    window.scrollTo({ top: 0 });
  },

  fire: (msg, type = 'info') => {
    const id = Date.now();
    set({ toast: { msg, type, id } });
    setTimeout(() => set(s => s.toast?.id === id ? { toast: null } : {}), 2600);
  },

  setOpenTeamModal: (v) => set({ openTeamModal: v }),

  // === API вызовы ===

  registerUser: async (data) => {
    try {
      const response = await apiFetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Регистрация не удалась');
      }
      const result = await response.json();
      localStorage.setItem('access_token', result.access_token);
      localStorage.setItem('refresh_token', result.refresh_token);
      set({ user: { ...result.user, status: 'none' } });

      // Автопривязка анонимной турнирной заявки
      const pendingAppId = localStorage.getItem('pending_app_id');
      if (pendingAppId) {
        try {
          const claimBody = { app_id: parseInt(pendingAppId) };
          const pendingToken = localStorage.getItem('pending_claim_token');
          if (pendingToken) claimBody.claim_token = pendingToken;
          const claimRes = await apiFetch(`${API_URL}/tournament/apply/claim`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${result.access_token}` },
            body: JSON.stringify(claimBody),
          });
          if (claimRes.ok) {
            localStorage.removeItem('pending_app_id');
            localStorage.removeItem('pending_claim_token');
            await get().fetchMe();
          }
        } catch (_) {}
      }

      get().fire(`Аккаунт создан · ${result.user.nickname || result.user.email}`, 'ok');
      const retReg = get()._postAuthRoute;
      set({ _postAuthRoute: null });
      get().go(retReg || AFTER_AUTH_ROUTE);
      return result;
    } catch (e) {
      get().fire(`Ошибка регистрации: ${e.message}`, 'error');
      throw e;
    }
  },

  loginUser: async (data) => {
    try {
      const response = await apiFetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка входа');
      }
      const result = await response.json();
      localStorage.setItem('access_token', result.access_token);
      localStorage.setItem('refresh_token', result.refresh_token);
      set({ user: result.user });

      // Привязываем анонимную турнирную заявку если есть
      const pendingAppId = localStorage.getItem('pending_app_id');
      if (pendingAppId) {
        try {
          const claimBody = { app_id: parseInt(pendingAppId) };
          const pendingToken = localStorage.getItem('pending_claim_token');
          if (pendingToken) claimBody.claim_token = pendingToken;
          const claimRes = await apiFetch(`${API_URL}/tournament/apply/claim`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${result.access_token}` },
            body: JSON.stringify(claimBody),
          });
          if (claimRes.ok) {
            localStorage.removeItem('pending_app_id');
            localStorage.removeItem('pending_claim_token');
          }
        } catch (_) {}
      }

      // Подтягиваем заявку и команду, чтобы профиль был полным сразу после входа
      await get().fetchMe();
      get().fire(`Вход выполнен · ${result.user.nickname || result.user.email}`, 'ok');
      // Роутим: админ → админка; иначе — куда просила витрина, по умолчанию register
      const ret = get()._postAuthRoute;
      set({ _postAuthRoute: null });
      const targetRoute = roleRoute(result.user.user_type, ret || AFTER_AUTH_ROUTE);
      get().go(targetRoute);
      return result;
    } catch (e) {
      get().fire(`Ошибка входа: ${e.message}`, 'error');
      throw e;
    }
  },

  logout: async () => {
    await detachPushFromAccount();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    set({ user: null, notifications: [], myApplications: [] });
    get().fire('Вы вышли из аккаунта');
    get().go('home');
  },

  // === Вход через Google / Telegram ===

  /** Есть ли необработанный ответ OAuth-редиректа (проверяет App при старте) */
  hasOAuthRedirect: () => !!OAUTH_REDIRECT && !oauthConsumed,

  /** Какие способы входа настроены на бэкенде (кнопки рисуем по ответу) */
  fetchOAuthConfig: async () => {
    try {
      const res = await apiFetch(`${API_URL}/oauth/config`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;  // бэкенд недоступен — соц-кнопки просто не покажем
    }
  },

  /** Уводим браузер к провайдеру. next — куда вернуть после входа. */
  startOAuthLogin: (provider, next) => {
    const q = next ? `?next=${encodeURIComponent(next)}` : '';
    window.location.href = `${API_URL}/oauth/login/${provider}${q}`;
  },

  /** Достраиваем сессию из токенов, которые провайдер вернул во фрагменте */
  completeOAuthLogin: async () => {
    if (!OAUTH_REDIRECT || oauthConsumed) return null;
    oauthConsumed = true;
    const { accessToken, refreshToken, isNew, next, error } = OAUTH_REDIRECT;

    if (error || !accessToken) {
      const msg = error in OAUTH_ERRORS ? OAUTH_ERRORS[error] : 'Не удалось войти через Google';
      if (msg) get().fire(msg, 'error');
      get().go('auth');
      return null;
    }

    localStorage.setItem('access_token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);

    await claimPendingApplication(accessToken);
    const me = await get().fetchMe();
    if (!me) {
      get().fire('Не удалось войти через Google', 'error');
      get().go('auth');
      return null;
    }

    const user = me.user;
    get().fire(
      isNew ? `Аккаунт создан · ${user.nickname || user.email}` : `Вход выполнен · ${user.nickname || user.email}`,
      'ok'
    );
    get().go(roleRoute(user.user_type, next || AFTER_AUTH_ROUTE));
    return me;
  },

  /** Добор профиля после OAuth: ник + телефон (+ email, если его не дал провайдер) */
  completeProfile: async ({ nickname, phone, email }) => {
    try {
      const res = await apiFetch(`${API_URL}/auth/complete-profile`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ nickname, phone, email }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || 'Не удалось сохранить профиль');
      }
      const { user } = await res.json();
      // Сначала мержим ответ сервера, а затем перечитываем /auth/me,
      // чтобы стор наверняка получил свежий needs_onboarding=false.
      set((s) => ({ user: { ...s.user, ...user } }));
      const fresh = await get().fetchMe();
      get().fire('Профиль заполнен', 'ok');
      return fresh?.user || user;
    } catch (e) {
      get().fire(e.message, 'error');
      throw e;
    }
  },

  /** Telegram Login Widget: подписанные данные виджета меняем на JWT */
  telegramLogin: async (tgUser) => {
    try {
      const res = await apiFetch(`${API_URL}/oauth/telegram`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tgUser),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Не удалось войти через Telegram');
      }
      const result = await res.json();
      localStorage.setItem('access_token', result.access_token);
      localStorage.setItem('refresh_token', result.refresh_token);
      set({ user: result.user });

      await claimPendingApplication(result.access_token);
      await get().fetchMe();

      const name = result.user.nickname || result.user.full_name;
      get().fire(result.is_new_user ? `Аккаунт создан · ${name}` : `Вход выполнен · ${name}`, 'ok');
      const ret = get()._postAuthRoute;
      set({ _postAuthRoute: null });
      get().go(roleRoute(result.user.user_type, ret || AFTER_AUTH_ROUTE));
      return result;
    } catch (e) {
      get().fire(`Ошибка входа: ${e.message}`, 'error');
      throw e;
    }
  },

  // === Заявки на витринные турниры (форма из конструктора админки) ===

  fetchMyApplications: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) { set({ myApplications: [] }); return; }
    try {
      const res = await apiFetch(`${API_URL}/tournament/applications/mine`, { headers: authHeaders(false) });
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data.applications)) set({ myApplications: data.applications });
    } catch (e) { /* бэкенд недоступен — статусы просто не покажем */ }
  },

  /** Подать заявку на витринный турнир. answers — {fieldKey: value}, files — {fieldKey: File}. */
  submitFedApplication: async ({ tournamentKey, answers, files = {} }) => {
    try {
      const fd = new FormData();
      fd.append('tournament_key', tournamentKey);
      fd.append('answers', JSON.stringify(answers));
      for (const [key, file] of Object.entries(files)) {
        if (file) fd.append(`file_${key}`, file);
      }
      const token = localStorage.getItem('access_token');
      const res = await apiFetch(`${API_URL}/tournament/apply-fed`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const ct = res.headers.get('content-type') || '';
        const err = ct.includes('json') ? (await res.json()).error : null;
        throw new Error(err || 'Не удалось отправить заявку');
      }
      const result = await res.json();
      set((s) => ({
        myApplications: [
          result.application,
          ...s.myApplications.filter((a) => a.tournament_key !== tournamentKey),
        ],
      }));
      get().fire('Заявка отправлена на модерацию', 'ok');
      return result.application;
    } catch (e) {
      get().fire(`Ошибка отправки: ${e.message}`, 'error');
      throw e;
    }
  },

  // Восстановление сессии: профиль + заявка + команда одним запросом
  fetchMe: async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    try {
      const response = await apiFetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        // Токен протух/недействителен — чистим сессию
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        set({ user: null });
        return null;
      }
      const { user, application, team, pending_request } = await response.json();
      const app = application || {};
      set({
        user: {
          ...user,
          // Плоский профиль, как ожидают страницы
          status: app.status || 'none',
          steam_profile: app.steam_profile,
          role: app.role,
          premier_rating: app.premier_rating,
          education_name: app.education_name,
          grade: app.grade,
          doc: !!app.document_file,
          rejection_reason: app.rejection_reason,
          application_id: app.id || null,
          team: team?.name || null,
          team_data: team || null,
          pending_request: pending_request || null,
        }
      });
      get().fetchMyApplications();
      return { user, application, team };
    } catch (e) {
      console.error('Ошибка восстановления сессии:', e);
      return null;
    }
  },

  submitTournamentApplication: async (formData) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await apiFetch(`${API_URL}/tournament/apply`, {
        method: 'POST',
        headers,
        body: formData
      });
      if (!response.ok) {
        const ct = response.headers.get('content-type') || '';
        if (ct.includes('application/json')) {
          const error = await response.json();
          throw new Error(error.error || `Ошибка сервера: ${response.status}`);
        }
        if (response.status === 413) throw new Error('Файл слишком большой (макс. 20 МБ)');
        throw new Error(`Ошибка сервера: ${response.status}`);
      }
      const result = await response.json();
      const app = result.application || {};
      if (!token && app.id) {
        localStorage.setItem('pending_app_id', app.id);
        if (result.claim_token) {
          localStorage.setItem('pending_claim_token', result.claim_token);
        }
      }
      if (token) {
        set(s => ({
          user: {
            ...s.user,
            status: 'submitted',
            doc: !!app.document_file,
            steam_profile: app.steam_profile,
            role: app.role,
            premier_rating: app.premier_rating,
            education_name: app.education_name,
            grade: app.grade,
          }
        }));
      }
      get().fire('Заявка отправлена на модерацию', 'ok');
      return result;
    } catch (e) {
      console.error('[ERROR] Tournament apply:', e);
      get().fire(`Ошибка отправки: ${e.message}`, 'error');
      throw e;
    }
  },

  requestJoinTeam: async (teamId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Не авторизован');
      const response = await apiFetch(`${API_URL}/teams/${teamId}/request`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка отправки запроса');
      }
      const result = await response.json();
      set(s => ({
        user: { ...s.user, pending_request: result.request }
      }));
      get().fire('Запрос отправлен капитану команды', 'ok');
      return result.request;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  fetchTeamRequests: async (teamId) => {
    try {
      const res = await apiFetch(`${API_URL}/teams/${teamId}/requests`, {
        headers: authHeaders(false),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка загрузки запросов');
      return (await res.json()).requests || [];
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      return [];
    }
  },

  respondToJoinRequest: async (reqId, action) => {
    try {
      const res = await apiFetch(`${API_URL}/teams/requests/${reqId}/respond`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка обработки запроса');
      const result = await res.json();
      get().fire(action === 'accept' ? 'Игрок принят в команду' : 'Запрос отклонён', action === 'accept' ? 'ok' : 'info');
      if (action === 'accept') await get().fetchMe();
      return result;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  moderateTeam: async (teamId, action, reason = null) => {
    try {
      const res = await apiFetch(`${API_URL}/teams/${teamId}/moderate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ action, reason }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка модерации');
      const result = await res.json();
      get().fire(action === 'approve' ? 'Команда подтверждена' : 'Команда отклонена', action === 'approve' ? 'ok' : 'info');
      return result.team;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  fetchTeams: async (status = null) => {
    try {
      let url = `${API_URL}/teams`;
      if (status) url += `?status=${status}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Ошибка загрузки команд');
      const result = await response.json();
      return result.teams || [];
    } catch (e) {
      console.error('Ошибка при загрузке команд:', e);
      return [];
    }
  },

  joinTeamByCode: async (code, role = 'lurk') => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Не авторизован');
      
      const response = await apiFetch(`${API_URL}/teams/join-by-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ code, role })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка вступления в команду');
      }
      const result = await response.json();
      set(s => ({
        user: { ...s.user, team: result.team.name }
      }));
      get().fire('Вы вступили в команду', 'ok');
      return result.team;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  createTeam: async ({ name, tag, role = 'igl' }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('Не авторизован');

      const response = await apiFetch(`${API_URL}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, tag, role })
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка создания команды');
      }
      const result = await response.json();
      set(s => ({
        user: { ...s.user, team: result.team.name, team_data: result.team }
      }));
      get().fire(`Команда создана · код ${result.team.join_code}`, 'ok');
      return result.team;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  removeTeamMember: async (teamId, userId) => {
    try {
      const res = await apiFetch(`${API_URL}/teams/${teamId}/members/${userId}`, {
        method: 'DELETE', headers: authHeaders(false),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка');
      await get().fetchMe();
      get().fire('Игрок исключён из команды', 'ok');
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error'); throw e;
    }
  },

  deleteTeam: async (teamId) => {
    try {
      const res = await apiFetch(`${API_URL}/teams/${teamId}`, {
        method: 'DELETE', headers: authHeaders(false),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка');
      await get().fetchMe();
      get().fire('Команда расформирована', 'ok');
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error'); throw e;
    }
  },

  // Удаление любой команды администратором (без обновления собственного профиля)
  adminDeleteTeam: async (teamId) => {
    try {
      const res = await apiFetch(`${API_URL}/teams/${teamId}`, {
        method: 'DELETE', headers: authHeaders(false),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка');
      get().fire('Команда удалена', 'ok');
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error'); throw e;
    }
  },

  leaveTeam: async (teamId) => {
    try {
      const res = await apiFetch(`${API_URL}/teams/${teamId}/leave`, {
        method: 'POST', headers: authHeaders(false),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка');
      await get().fetchMe();
      get().fire('Вы покинули команду', 'ok');
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error'); throw e;
    }
  },

  // === Поддержка ===

  submitSupportTicket: async (data) => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await apiFetch(`${API_URL}/support/ticket`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка отправки');
      get().fire('Обращение отправлено — скоро ответим!', 'ok');
      return await res.json();
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  fetchSupportTickets: async (status = null) => {
    try {
      let url = `${API_URL}/support/tickets?per_page=100`;
      if (status) url += `&status=${status}`;
      const res = await apiFetch(url, { headers: authHeaders(false) });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка загрузки');
      return await res.json();
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      return { tickets: [], total: 0, new_count: 0 };
    }
  },

  markSupportTicketRead: async (id) => {
    try {
      const res = await apiFetch(`${API_URL}/support/tickets/${id}/read`, {
        method: 'PUT',
        headers: authHeaders(false),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка');
      return (await res.json()).ticket;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  // === Турниры (публично) ===

  fetchActiveTournament: async () => {
    try {
      const res = await apiFetch(`${API_URL}/tournaments/active`);
      if (!res.ok) return null;
      return (await res.json()).tournament || null;
    } catch (e) {
      console.error('Ошибка загрузки турнира:', e);
      return null;
    }
  },

  fetchTournaments: async () => {
    try {
      const res = await apiFetch(`${API_URL}/tournaments`);
      if (!res.ok) return [];
      return (await res.json()).tournaments || [];
    } catch (e) {
      console.error('Ошибка загрузки турниров:', e);
      return [];
    }
  },

  fetchTournamentDetail: async (id) => {
    try {
      const res = await apiFetch(`${API_URL}/tournaments/${id}`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error('Ошибка загрузки турнира:', e);
      return null;
    }
  },

  // === Админ ===

  fetchApplications: async (status = null, tournamentKey = null) => {
    try {
      let url = `${API_URL}/tournament/applications?per_page=200`;
      if (status) url += `&status=${status}`;
      if (tournamentKey) url += `&tournament_key=${encodeURIComponent(tournamentKey)}`;
      const res = await apiFetch(url, { headers: authHeaders(false) });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка загрузки заявок');
      return (await res.json()).applications || [];
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      return [];
    }
  },

  // Заявка конкретного игрока (для проверки документов из карточки команды).
  // tournamentId — чтобы показать заявку именно на турнир этой команды.
  fetchUserApplication: async (userId, tournamentId = null) => {
    try {
      let url = `${API_URL}/tournament/applications/by-user/${userId}`;
      if (tournamentId) url += `?tournament_id=${tournamentId}`;
      const res = await apiFetch(url, { headers: authHeaders(false) });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка загрузки заявки');
      return (await res.json()).application || null;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      return null;
    }
  },

  updateApplicationStatus: async (id, status, rejection_reason = null) => {
    try {
      const res = await apiFetch(`${API_URL}/tournament/applications/${id}/status`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status, rejection_reason })
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка обновления статуса');
      return (await res.json()).application;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  createTournament: async (data) => {
    try {
      const res = await apiFetch(`${API_URL}/tournaments`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка создания турнира');
      const result = await res.json();
      get().fire('Турнир создан', 'ok');
      return result.tournament;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  updateTournament: async (id, data) => {
    try {
      const res = await apiFetch(`${API_URL}/tournaments/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка обновления турнира');
      const result = await res.json();
      get().fire('Турнир обновлён', 'ok');
      return result.tournament;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  fetchUsers: async (q = '') => {
    try {
      // per_page высокий, чтобы подгружать всех пользователей одним запросом
      // (на масштабе федерации это сотни записей — пагинация не нужна)
      let url = `${API_URL}/admin/users?per_page=1000`;
      if (q) url += `&q=${encodeURIComponent(q)}`;
      const res = await apiFetch(url, { headers: authHeaders(false) });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка загрузки пользователей');
      return (await res.json()).users || [];
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      return [];
    }
  },

  setUserRole: async (userId, role) => {
    try {
      const res = await apiFetch(`${API_URL}/admin/users/${userId}/set-role`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ role }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка обновления роли');
      const result = await res.json();
      get().fire(role === 'captain' ? 'Роль капитана выдана' : 'Роль снята', 'ok');
      return result.user;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  // Блокировка / разблокировка пользователя администратором. active=false —
  // бан: аккаунт мгновенно теряет доступ (вход, токен, запросы отклоняются).
  setUserActive: async (userId, active) => {
    try {
      const res = await apiFetch(`${API_URL}/admin/users/${userId}/set-active`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ active }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Ошибка блокировки');
      const result = await res.json();
      get().fire(active ? 'Пользователь разблокирован' : 'Пользователь заблокирован', 'ok');
      return result.user;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },

  // Сброс пароля пользователя администратором. Если password не передан —
  // бэкенд сгенерирует его. Возвращает { password, generated, nickname, ... }
  // — новый пароль показывается один раз, чтобы передать владельцу аккаунта.
  resetUserPassword: async (userId, password = null) => {
    try {
      const res = await apiFetch(`${API_URL}/admin/users/${userId}/reset-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(password ? { password } : {}),
      });
      // Тело читаем один раз текстом: при рестарте бэка nginx отдаёт HTML, и
      // res.json() падал с «Unexpected token '<'», пряча настоящую причину.
      const raw = await res.text();
      let data = null;
      try { data = JSON.parse(raw); } catch (e) { /* ответ не JSON — ниже */ }
      if (!res.ok) {
        throw new Error(
          (data && data.error) ||
          (res.status === 401 ? 'Сессия истекла — войдите в админку заново'
                              : `Сервер вернул ошибку ${res.status}`)
        );
      }
      const result = data;
      get().fire('Пароль сброшен', 'ok');
      return result;
    } catch (e) {
      get().fire(`Ошибка: ${e.message}`, 'error');
      throw e;
    }
  },
}));
